<template>
  <div>
    <ui-button color="success" @click.native="dialog = true">
      <fa icon="fa-solid fa-plus" class="tw-mr-2" />
      Add User
    </ui-button>

    <ui-modal v-if="dialog" @close="dialog = false" modal-title="Add User" custom-class="tw-max-w-lg tw-w-full">
      <div class="tw-flex tw-flex-col tw-gap-6 tw-mt-4">
        <div v-if="hasSso" class="tw-flex tw-border-b tw-border-gray-200">
          <button
            type="button"
            class="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw--mb-px tw-border-b-2 tw-transition-colors"
            :class="
              activeTab === 'standard'
                ? 'tw-text-primary-400 tw-border-primary-400'
                : 'tw-text-neutral-500 tw-border-transparent hover:tw-text-primary-300'
            "
            @click="activeTab = 'standard'"
          >
            Standard
          </button>
          <button
            type="button"
            class="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw--mb-px tw-border-b-2 tw-transition-colors"
            :class="
              activeTab === 'saml'
                ? 'tw-text-primary-400 tw-border-primary-400'
                : 'tw-text-neutral-500 tw-border-transparent hover:tw-text-primary-300'
            "
            @click="activeTab = 'saml'"
          >
            SAML
          </button>
        </div>

        <ui-alert v-if="inviteError" type="error" heading="Unable to add user" role="alert" aria-live="assertive">
          {{ inviteError }}
        </ui-alert>

        <div class="tw-space-y-4">
          <ui-text-edit
            :label="activeTab === 'saml' ? 'Name' : 'Invitee Name'"
            v-model="name"
            :rules="[rules.required]"
            :is-valid="!dirtyFields.name || isNameValid"
            :validation-message="dirtyFields.name && !isNameValid ? nameError : ''"
            :validated="dirtyFields.name"
            @input="dirtyFields.name = true"
          ></ui-text-edit>

          <ui-text-edit
            :label="activeTab === 'saml' ? 'Email' : 'Invitee Email'"
            v-model="email"
            :rules="[rules.required, rules.email]"
            :is-valid="!dirtyFields.email || isEmailValid"
            :validation-message="dirtyFields.email && !isEmailValid ? emailError : ''"
            :validated="dirtyFields.email"
            @input="dirtyFields.email = true"
          ></ui-text-edit>

          <ui-text-edit
            v-if="activeTab === 'saml'"
            label="Asserted Identity (SAML NameID)"
            v-model="assertedIdentity"
            :rules="[rules.required]"
            :is-valid="!dirtyFields.assertedIdentity || isAssertedIdentityValid"
            :validation-message="dirtyFields.assertedIdentity && !isAssertedIdentityValid ? assertedIdentityError : ''"
            :validated="dirtyFields.assertedIdentity"
            @input="dirtyFields.assertedIdentity = true"
          ></ui-text-edit>

          <ui-select2
            :items="roles"
            :label="$tc('_role')"
            :rules="[rules.required, rules.role]"
            v-model="role"
            vertical-list-align="top"
            keyField="id"
            labelField="name"
            keyOnly
            searchable
            :is-valid="!dirtyFields.role || isRoleValid"
            :validation-message="dirtyFields.role && !isRoleValid ? roleError : ''"
            :validated="dirtyFields.role"
            @changed="dirtyFields.role = true"
          ></ui-select2>

          <div v-if="activeTab !== 'saml'" aria-hidden="true" class="tw-invisible">
            <ui-text-edit label="placeholder" value=""></ui-text-edit>
          </div>
        </div>

        <div class="tw-flex tw-justify-end tw-gap-3 tw-pt-4 tw-border-t tw-border-gray-200">
          <ui-button color="secondary" @click.native="dialog = false"> Cancel </ui-button>
          <ui-button color="primary" :loading="loading" @click.native="save" :disabled="!canSave"> Save </ui-button>
        </div>
      </div>
    </ui-modal>
  </div>
</template>

<script>
import axios from 'axios';
import gql from 'graphql-tag';

import UiAlert from '../ui/UiAlert.vue';
import UiButton from '../ui/UiButton.vue';
import UiModal from '../ui/UiModal.vue';
import UiSelect2 from '../ui/UiSelect2.vue';
import UiTextEdit from '../ui/UiTextEdit.vue';

export default {
  props: {
    user: {
      type: Object,
      default: null,
    },
    hasSso: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    UiAlert,
    UiTextEdit,
    UiButton,
    UiModal,
    UiSelect2,
  },
  data() {
    return {
      dialog: false,
      activeTab: 'standard',
      name: '',
      email: '',
      assertedIdentity: '',
      role: '',
      inviteError: '',
      saving: false,
      valid: false,
      dirtyFields: {
        name: false,
        email: false,
        assertedIdentity: false,
        role: false,
      },
      rules: {
        required: (v) => !!v || 'Field is required',
        email: (v) => !!v.includes('@') || 'Email must be valid',
        role: (v) => !!v || 'Role must be selected',
      },
      roles: [],
      isRolesLoading: 0,
    };
  },
  computed: {
    loading() {
      return this.isRolesLoading > 0 || this.saving;
    },
    isNameValid() {
      return this.rules.required(this.name) === true;
    },
    isEmailValid() {
      return this.rules.required(this.email) === true && this.rules.email(this.email) === true;
    },
    isAssertedIdentityValid() {
      return this.rules.required(this.assertedIdentity) === true;
    },
    isRoleValid() {
      return this.rules.role(this.role) === true;
    },
    nameError() {
      const requiredResult = this.rules.required(this.name);
      return requiredResult !== true ? requiredResult : '';
    },
    emailError() {
      const requiredResult = this.rules.required(this.email);
      if (requiredResult !== true) return requiredResult;
      const emailResult = this.rules.email(this.email);
      return emailResult !== true ? emailResult : '';
    },
    assertedIdentityError() {
      const result = this.rules.required(this.assertedIdentity);
      return result !== true ? result : '';
    },
    roleError() {
      const roleResult = this.rules.role(this.role);
      return roleResult !== true ? roleResult : '';
    },
    canSave() {
      const baseValid = this.isNameValid && this.isEmailValid && this.isRoleValid;
      if (this.activeTab === 'saml') {
        return baseValid && this.isAssertedIdentityValid;
      }
      return baseValid;
    },
  },
  watch: {
    dialog(isOpen) {
      if (isOpen) this.inviteError = '';
    },
    name() {
      this.inviteError = '';
    },
    email() {
      this.inviteError = '';
    },
    role() {
      this.inviteError = '';
    },
    activeTab() {
      this.inviteError = '';
    },
  },
  methods: {
    resetForm() {
      this.name = '';
      this.email = '';
      this.assertedIdentity = '';
      this.role = '';
      this.inviteError = '';
      this.dirtyFields = { name: false, email: false, assertedIdentity: false, role: false };
    },
    setInviteError(errors, fallbackMessage) {
      const messages = (errors ?? [])
        .map((error) => (typeof error === 'string' ? error : error?.message))
        .filter(Boolean);

      this.inviteError = messages.some((message) => message.toLowerCase().includes('already in org'))
        ? 'This user is already a member of this organization.'
        : messages.join(' ') || fallbackMessage;
    },
    async save() {
      if (this.activeTab === 'saml') {
        await this.saveSaml();
      } else {
        await this.saveStandard();
      }
    },
    async saveStandard() {
      this.inviteError = '';
      const vars = {
        orgId: this.$store.state.currentOrg.id,
        invite: {
          userName: this.name,
          email: this.email,
          roleId: this.role,
        },
      };

      const resp = await this.$apollo.mutate({
        mutation: gql`
          mutation ($orgId: ID!, $invite: InviteInput!) {
            inviteUser(orgId: $orgId, invite: $invite) {
              success
              errors
            }
          }
        `,
        variables: vars,
      });
      if (resp.errors) {
        this.setInviteError(resp.errors, 'We could not invite this user. Please try again.');
      }

      if (resp.data && resp.data.inviteUser) {
        if (resp.data.inviteUser.success) {
          this.showSuccessSnackbar('User Invited!');
          this.resetForm();
          this.dialog = false;
          this.$emit('complete');
        } else {
          this.setInviteError(resp.data.inviteUser.errors, 'We could not invite this user. Please try again.');
        }
      }
    },
    async saveSaml() {
      this.saving = true;
      try {
        const orgId = this.$store.state.currentOrg.id;
        const url = `${process.env.VUE_APP_API_URL}v2/orgs/${orgId}/users/provision-saml`;
        const resp = await axios.post(
          url,
          {
            email: this.email,
            displayName: this.name,
            assertedIdentity: this.assertedIdentity,
            roleId: this.role,
          },
          { withCredentials: true }
        );
        if (resp.status >= 200 && resp.status < 300) {
          this.showSuccessSnackbar('SAML User Provisioned!');
          this.resetForm();
          this.dialog = false;
          this.$emit('complete');
        } else {
          this.showErrorSnackbar('Problem provisioning SAML user');
        }
      } catch (err) {
        const message = err?.response?.data?.message ?? err?.message ?? 'Unknown error';
        this.showErrorSnackbar('Problem provisioning SAML user: ' + message);
      } finally {
        this.saving = false;
      }
    },
  },
  apollo: {
    roles: {
      query: gql`
        query getRoles($orgId: ID!) {
          roles(orgId: $orgId) {
            id
            name
            description
          }
        }
      `,
      variables() {
        return {
          orgId: this.$store.state.currentOrg.id,
        };
      },
      update(data) {
        return [...data.roles].sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
        );
      },
      loadingKey: 'isRolesLoading',
    },
  },
};
</script>
